import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { track } from "@/lib/analytics";
import {
  Menu,
  X,
  Phone,
  ChevronDown,
  Sun,
  BatteryCharging,
  Droplet,
  Car,
  Lightbulb,
  Package,
  Wrench,
  ThermometerSun,
  Snowflake,
  Home,
  FileText,
} from "lucide-react";

const services = [
  { to: "/zonnepanelen", icon: Sun, title: "Zonnepanelen", description: "Slimme PV-installaties" },
  { to: "/warmtepompen", icon: ThermometerSun, title: "Warmtepompen", description: "Efficiënte verwarming" },
  { to: "/airco", icon: Snowflake, title: "Airco", description: "Koelen & verwarmen" },
  { to: "/thuisbatterijen", icon: BatteryCharging, title: "Thuisbatterijen", description: "Maximale zelfconsumptie" },
  { to: "/zonneboilers", icon: Droplet, title: "Zonneboilers", description: "Duurzaam warm water" },
  { to: "/elektra", icon: Lightbulb, title: "Elektra & Verlichting", description: "Veilige installatie" },
  { to: "/laadpalen", icon: Car, title: "Laadpalen", description: "Slim laden thuis" },
  { to: "/smarthome", icon: Home, title: "Smart Home", description: "Loxone domotica systemen" },
  { to: "/zonnepaneel-pakketten", icon: Package, title: "Pakketten", description: "Vaste sets" },
  { to: "/maatwerk", icon: Wrench, title: "Maatwerk", description: "Ontwerp op maat" },
  { to: "/installatie-registreren", icon: FileText, title: "Installatie Registreren", description: "Aanmelden bij netbeheerder" },
];

export function MobileNavigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const handleLinkClick = (label: string) => {
    track("mobile_nav_click", { label });
    setIsOpen(false);
  };

  const handleCTAClick = (type: string) => {
    track(`mobile_nav_${type}`);
    setIsOpen(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon"
          className="md:hidden h-10 w-10 hover:bg-muted/50"
          onClick={() => track("mobile_nav_open")}
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Open menu</span>
        </Button>
      </SheetTrigger>
      
      <SheetContent side="right" className="w-full sm:w-80 p-0 bg-background">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <Link to="/" onClick={() => handleLinkClick("Logo")}>
              <img 
                src="/lovable-uploads/4e6c4533-b5c4-46a9-b8f7-7ea12550905b.png" 
                alt="SOL-VI Logo" 
                className="h-8 w-auto"
              />
            </Link>
          </div>

          {/* Navigation Content */}
          <nav className="flex-1 overflow-y-auto p-4">
            <div className="space-y-2">
              {/* Services Collapsible */}
              <Collapsible open={servicesOpen} onOpenChange={setServicesOpen}>
                <CollapsibleTrigger className="flex items-center justify-between w-full p-3 text-left hover:bg-muted/50 rounded-lg transition-colors">
                  <span className="font-medium">Diensten</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1 mt-1">
                  {services.map((service) => {
                    const Icon = service.icon;
                    const isActive = location.pathname === service.to;
                    return (
                      <Link
                        key={service.to}
                        to={service.to}
                        onClick={() => handleLinkClick(`Diensten - ${service.title}`)}
                        className={`flex items-center gap-3 p-3 ml-4 rounded-lg transition-colors ${
                          isActive 
                            ? "bg-primary text-primary-foreground" 
                            : "hover:bg-muted/50"
                        }`}
                      >
                        <Icon className="h-4 w-4 text-primary" />
                        <div>
                          <div className="font-medium text-sm">{service.title}</div>
                          <div className="text-xs text-muted-foreground">{service.description}</div>
                        </div>
                      </Link>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>

              {/* Direct Navigation Links */}
              {[
                { to: "/over-ons", label: "Over Ons" },
                { to: "/contact", label: "Contact" },
              ].map((link) => {
                const isActive = location.pathname === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => handleLinkClick(link.label)}
                    className={`block p-3 rounded-lg transition-colors ${
                      isActive 
                        ? "bg-primary text-primary-foreground" 
                        : "hover:bg-muted/50"
                    }`}
                  >
                    <span className="font-medium">{link.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Footer CTAs */}
          <div className="p-4 border-t space-y-3">
            {/* Phone CTA */}
            <a 
              href="tel:0617105786" 
              className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
              onClick={() => handleCTAClick("call")}
            >
              <Phone className="h-4 w-4 text-primary" />
              <div>
                <div className="font-medium text-sm">Bel direct</div>
                <div className="text-xs text-muted-foreground">06 17 10 57 86</div>
              </div>
            </a>
            
            {/* Primary CTAs */}
            <div className="space-y-2">
              <Link to="/zonnepaneel-pakketten" className="block" onClick={() => handleCTAClick("pakket")}>
                <Button variant="outline" className="w-full">
                  Bereken Pakket
                </Button>
              </Link>
              
              <Button
                variant="cta"
                className="w-full"
                onClick={() => {
                  handleCTAClick("calendly");
                  (window as any).Calendly?.initPopupWidget?.({
                    url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800',
                    pageSettings: { backgroundColor: 'ffffff', hideEventTypeDetails: false, hideLandingPageDetails: false, primaryColor: 'ff7800', textColor: '4d5055' },
                    prefill: {}, utm: {}
                  });
                }}
              >
                Plan Adviesgesprek
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}